/**
 * <TemplateFileOverrideModal> — user-gated approval surface for the
 * `requestTemplateFileOverride` MCP tool (crucible#3491, remedy (c)
 * of hello-weekend#2082).
 *
 * When a template-managed file under `packages/**` (an Ingot, shared
 * code, or a skill self-test) blocks a build or deploy and no normal
 * in-sandbox remedy applies, the agent calls this tool to ask the
 * user to authorise deleting that ONE named file. Main pushes the
 * request via `template-file-override:request`; this component
 * renders it as a modal with two actions:
 *
 *   - **Delete file** (danger-styled) — approve the delete.
 *   - **Decline** (subdued) — refuse the request.
 *
 * Esc / Back-button dismissal maps to Decline with a fixed reason.
 *
 * Deliberately simpler than `<DependencyApprovalModal>`: there is no
 * pin/variant input — this tool can only DELETE the named file, never
 * rewrite its contents (see the tool's own description). Styled with
 * a destructive-action accent (not the neutral crucible-accent) so
 * the user reads this as materially different from an additive
 * dependency install.
 *
 * Renderer-only: this component does not call any IPC directly. All
 * side effects flow through props (`onApprove` / `onDecline`); the
 * Workspace consumer wires them into chatDispatch which the chat
 * reducer's effect interpreter forwards onto the
 * `template-file-override:respond` IPC channel.
 */

import { useEffect, useRef } from 'react';

export interface TemplateFileOverrideModalProps {
  /** Game-relative path of the file the agent wants deleted. */
  path: string;
  /** Agent's one-line justification (italic). */
  reason: string;
  /** Epoch ms the request was fired. Reserved for a future relative-time UX. */
  requestedAt: number;
  /** Called when the user approves the delete. */
  onApprove: () => void;
  /** Called with a short reason string (Decline / Esc / Backdrop). */
  onDecline: (reason: string) => void;
}

/**
 * Stable copy for the two decline paths. Exported so tests can pin
 * the wording — these strings end up in the agent's tool result.
 */
export const TEMPLATE_FILE_OVERRIDE_DECLINE_REASON = 'user clicked Decline';
export const TEMPLATE_FILE_OVERRIDE_DISMISS_REASON = 'user dismissed the modal (Esc / backdrop)';

export function TemplateFileOverrideModal({
  path,
  reason,
  requestedAt,
  onApprove,
  onDecline,
}: TemplateFileOverrideModalProps): React.JSX.Element {
  // Reserved for a future relative-time UX; exposing the prop now pins
  // the shape so a future addition doesn't require a breaking change.
  void requestedAt;

  // Auto-focus the dialog root on mount so Escape lands immediately —
  // mirrors DependencyApprovalModal's `tabIndex={-1}` + explicit
  // focus() contract (Macroscope round-2 finding on that component,
  // PR #581: without it the keydown handler is dead code).
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const handleApprove = (): void => {
    onApprove();
  };

  const handleDecline = (): void => {
    onDecline(TEMPLATE_FILE_OVERRIDE_DECLINE_REASON);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onDecline(TEMPLATE_FILE_OVERRIDE_DISMISS_REASON);
    }
  };

  // Backdrop click -> dismiss, same target===currentTarget
  // discriminator as DependencyApprovalModal (a click on the card body
  // bubbles here too; only the dimmed backdrop itself should dismiss).
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) {
      onDecline(TEMPLATE_FILE_OVERRIDE_DISMISS_REASON);
    }
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tfo-modal-title"
      data-testid="template-file-override-modal"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onClick={handleBackdropClick}
      // Same z-index posture as DependencyApprovalModal (z-[60], above
      // the preview-recovery toast) and the same app-region-no-drag
      // carve-out (issue #965) so clicks aren't absorbed by the
      // draggable header strip underneath.
      className="app-region-no-drag fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 outline-none"
    >
      <div className="w-full max-w-lg rounded-lg border border-red-500/40 bg-zinc-950 px-5 py-5 shadow-2xl">
        <div className="mb-4">
          <p id="tfo-modal-title" className="text-xs uppercase tracking-wide text-red-400">
            Delete template file request
          </p>
          <p
            className="mt-1 break-all font-mono text-lg font-semibold text-white"
            data-testid="template-file-override-modal-path"
          >
            {path}
          </p>
        </div>

        <div className="mb-4 rounded-md border border-white/10 bg-white/[0.02] px-3 py-2.5">
          <p className="text-[10px] uppercase tracking-wide text-white/40">Agent reason</p>
          <p
            className="mt-1 text-sm italic text-white/80"
            data-testid="template-file-override-modal-reason"
          >
            {reason}
          </p>
        </div>

        <p
          className="mb-4 text-xs leading-relaxed text-white/50"
          data-testid="template-file-override-modal-warning"
        >
          This file is part of the Foundry template, not your game&apos;s own code. Deleting it
          cannot be undone in Foundry. Only approve this if you understand why it&apos;s blocking
          your build.
        </p>

        <div className="flex items-center justify-end gap-2 border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={handleDecline}
            data-testid="template-file-override-modal-decline"
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/60 transition-colors hover:border-white/20 hover:text-white"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={handleApprove}
            data-testid="template-file-override-modal-approve"
            className="rounded-lg bg-red-600 px-4 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90"
          >
            Delete file
          </button>
        </div>
      </div>
    </div>
  );
}
